import React, { useEffect, useState } from 'react';
import { getGlobalSettings, getWebhookSettings, updateGlobalSettings, updateWebhookSettings } from '../../services/apiService';
import { useAuth } from '../../contexts/AuthContext';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Paper from '@mui/material/Paper';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import Divider from '@mui/material/Divider';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

const DEFAULTS = {
  descriptions: 'https://n8nw.segmently.ai/webhook/descriptions',
  audiences: 'https://n8nw.segmently.ai/webhook/audiences',
  actors: 'https://n8nw.segmently.ai/webhook/actors',
  questions: 'https://n8nw.segmently.ai/webhook/questions',
  interviews: 'https://n8nw.segmently.ai/webhook/interviews',
  ai_actor: 'https://n8nw.segmently.ai/webhook/ai_actor',
  strategyblocks: 'https://n8nw.segmently.ai/webhook/strategy-blocks',
  ai_strategy: 'https://n8nw.segmently.ai/webhook/ai_strategy',
  structuredoutput: 'https://n8nw.segmently.ai/webhook/screens',
  image_generation: 'https://n8nw.segmently.ai/webhook/image-generation',
  tasks: 'https://n8nw.segmently.ai/webhook/tasks',
  // Optional: dedicated reviews webhook (falls back to descriptions if unset)
  reviews: ''
};

export default function SettingsPage() {
  const { isAdmin } = useAuth();
  const [webhookUrls, setWebhookUrls] = useState(DEFAULTS);
  const [globalSettings, setGlobalSettings] = useState({
    emailNotifications: {
      enabled: true,
      automations: {
        taskCompleted: true,
        descriptions: true,
        audiences: true,
        actors: true,
        questions: true,
        interviews: true,
        aiActor: true,
        strategyBlocks: true,
        aiStrategy: true,
        structuredOutput: true,
        imageGeneration: true
      }
    },
    inAppNotifications: true
  });
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [emailSettingsExpanded, setEmailSettingsExpanded] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      setLoading(true);
      try {
        const [webhookData, globalSettingsData] = await Promise.all([
          getWebhookSettings(),
          getGlobalSettings()
        ]);
        setWebhookUrls({ ...DEFAULTS, ...webhookData });
        setGlobalSettings(globalSettingsData);
      } catch (_e) {
        setError('Failed to load settings.');
      }
      setLoading(false);
    };
    fetchSettings();
  }, []);

  const handleWebhookChange = (e) => {
    setWebhookUrls({ ...webhookUrls, [e.target.name]: e.target.value });
  };

  const handleGlobalSettingChange = (setting, value) => {
    setGlobalSettings(prev => ({ ...prev, [setting]: value }));
  };

  const handleEmailNotificationChange = (automation, value) => {
    setGlobalSettings(prev => ({
      ...prev,
      emailNotifications: {
        ...prev.emailNotifications,
        automations: {
          ...prev.emailNotifications.automations,
          [automation]: value
        }
      }
    }));
  };

  const handleSelectAllToggle = (enabled) => {
    setGlobalSettings(prev => ({
      ...prev,
      emailNotifications: {
        ...prev.emailNotifications,
        // Enable/disable all individual automation switches
        automations: {
          descriptions: enabled,
          audiences: enabled,
          actors: enabled,
          questions: enabled,
          interviews: enabled,
          aiActor: enabled,
          strategyBlocks: enabled,
          aiStrategy: enabled,
          structuredOutput: enabled,
          imageGeneration: enabled
        }
      }
    }));
  };



  const handleSaveAllSettings = async () => {
    if (!isAdmin) {
      setError('Only administrators can update global settings.');
      return;
    }
    
    setSuccess('');
    setError('');
    setLoading(true);
    
    try {
      // Save both webhook and global settings
      await Promise.all([
        updateWebhookSettings(webhookUrls),
        updateGlobalSettings(globalSettings)
      ]);
      setSuccess('All settings saved successfully!');
    } catch (_e) {
      setError('Failed to save settings.');
    } finally {
      setLoading(false);
    }
  };

  const automationLabels = {
    descriptions: 'Product Descriptions',
    audiences: 'Audience Generation',
    actors: 'Actor Generation',
    questions: 'Question Generation',
    interviews: 'Interview Processing',
    aiActor: 'AI Actor Creation',
    strategyBlocks: 'Strategy Blocks',
    aiStrategy: 'AI Strategy',
    structuredOutput: 'Structured Output',
    imageGeneration: 'Image Generation'
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Settings
      </Typography>
      
      {/* Global Settings Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" gutterBottom>
          Global Settings
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          These settings affect all users in the system.
        </Typography>
        
        {/* Email Notifications Section */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={Object.values(globalSettings.emailNotifications?.automations || {}).every(enabled => enabled)}
                    onChange={(e) => handleSelectAllToggle(e.target.checked)}
                    color="primary"
                    disabled={!isAdmin}
                  />
                }
                label="Select all email notifications"
              />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Enable or disable all email notifications for automations at once.
              </Typography>
            </Box>
            <IconButton
              onClick={() => setEmailSettingsExpanded(!emailSettingsExpanded)}
              disabled={!isAdmin}
            >
              {emailSettingsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
          
          <Collapse in={emailSettingsExpanded}>
            <Box sx={{ pl: 3, borderLeft: '2px solid #e0e0e0', mt: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                Individual Automation Settings
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Configure email notifications for specific automations. Each switch controls whether email notifications are sent for that particular automation type.
              </Typography>
              
              {Object.entries(automationLabels).map(([key, label]) => (
                <FormControlLabel
                  key={key}
                  control={
                    <Switch
                      checked={globalSettings.emailNotifications?.automations?.[key] || false}
                      onChange={(e) => handleEmailNotificationChange(key, e.target.checked)}
                      color="primary"
                      disabled={!isAdmin}
                    />
                  }
                  label={label}
                  sx={{ display: 'block', mb: 1 }}
                />
              ))}
            </Box>
          </Collapse>
        </Box>
        
        {/* In-App Notifications */}
        <FormControlLabel
          control={
            <Switch
              checked={globalSettings.inAppNotifications || false}
              onChange={(e) => handleGlobalSettingChange('inAppNotifications', e.target.checked)}
              disabled={!isAdmin}
            />
          }
          label="In-App Notifications"
        />
        
        {/* Storage Bucket ID - REMOVED - Now automatically generated based on project name */}
        
        {!isAdmin && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Only administrators can modify global settings.
          </Alert>
        )}
      </Paper>

      <Divider sx={{ my: 3 }} />

      {/* Webhook Settings Section */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          Webhook Settings
        </Typography>
        <TextField
          fullWidth
          label="Reviews Webhook URL (optional)"
          name="reviews"
          value={webhookUrls.reviews}
          onChange={handleWebhookChange}
          margin="normal"
        />
        <TextField
          fullWidth
          label="Descriptions Webhook URL"
          name="descriptions"
          value={webhookUrls.descriptions}
          onChange={handleWebhookChange}
          margin="normal"
        />
        <TextField
          fullWidth
          label="Audiences Webhook URL"
          name="audiences"
          value={webhookUrls.audiences}
          onChange={handleWebhookChange}
          margin="normal"
        />
        <TextField
          fullWidth
          label="Actors Webhook URL"
          name="actors"
          value={webhookUrls.actors}
          onChange={handleWebhookChange}
          margin="normal"
        />
        <TextField
          fullWidth
          label="Questions Webhook URL"
          name="questions"
          value={webhookUrls.questions}
          onChange={handleWebhookChange}
          margin="normal"
        />
        <TextField
          fullWidth
          label="Interviews Webhook URL"
          name="interviews"
          value={webhookUrls.interviews}
          onChange={handleWebhookChange}
          margin="normal"
        />
        <TextField
          fullWidth
          label="AI Actor Webhook URL"
          name="ai_actor"
          value={webhookUrls.ai_actor}
          onChange={handleWebhookChange}
          margin="normal"
        />
        <TextField
          fullWidth
          label="Strategy Blocks Webhook URL"
          name="strategyblocks"
          value={webhookUrls.strategyblocks}
          onChange={handleWebhookChange}
          margin="normal"
        />
        <TextField
          fullWidth
          label="AI Strategy Webhook URL"
          name="ai_strategy"
          value={webhookUrls.ai_strategy}
          onChange={handleWebhookChange}
          margin="normal"
        />
        <TextField
          fullWidth
          label="Structured Output Webhook URL"
          name="structuredoutput"
          value={webhookUrls.structuredoutput}
          onChange={handleWebhookChange}
          margin="normal"
        />
        <TextField
          fullWidth
          label="Image Generation Webhook URL"
          name="image_generation"
          value={webhookUrls.image_generation}
          onChange={handleWebhookChange}
          margin="normal"
        />
        <TextField
          fullWidth
          label="Tasks Webhook URL"
          name="tasks"
          value={webhookUrls.tasks}
          onChange={handleWebhookChange}
          margin="normal"
        />
      </Paper>

      {/* Single Save Button */}
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
        <Button
          variant="contained"
          color="primary"
          onClick={handleSaveAllSettings}
          disabled={loading || !isAdmin}
          size="large"
        >
          {loading ? 'Saving...' : 'Save All Settings'}
        </Button>
      </Box>
    </Box>
  );
} 
